"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { HandCoins, ArrowRight, X, Sparkles } from "lucide-react";

const STORAGE_KEY = "ns_ambassador_banner_dismissed";

interface AmbassadorBannerProps {
  text?: string;
  ctaText?: string;
  ctaHref?: string;
}

/**
 * Elegancki, przyklejony do góry banner promujący program ambasadorski.
 * Treść i CTA konfigurowane z panelu (Ustawienia strony). Zamknięcie
 * zapamiętywane jest w localStorage.
 */
export default function AmbassadorBanner({
  text = "Zarabiaj polecając kursy NextSchool. Zostań ambasadorem i odbieraj prowizję za każdą sprzedaż.",
  ctaText = "Dowiedz się więcej",
  ctaHref = "/ambasador",
}: AmbassadorBannerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) !== "1") setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -64, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -64, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="sticky top-0 z-50 overflow-hidden bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white shadow-[0_4px_24px_-8px_rgba(124,58,237,0.6)]"
        >
          {/* Subtelna poświata w tle */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background:
                "radial-gradient(600px 120px at 15% 50%, rgba(255,255,255,0.18), transparent 70%)",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2.5 sm:gap-3">
            <span className="hidden sm:flex w-8 h-8 rounded-full bg-white/15 ring-1 ring-white/25 items-center justify-center shrink-0 backdrop-blur-sm">
              <HandCoins className="w-4 h-4" />
            </span>
            <p className="flex-1 min-w-0 text-[13px] sm:text-sm font-medium leading-snug">
              <span className="inline-flex items-center gap-1 mr-1.5 align-middle">
                <Sparkles className="w-3.5 h-3.5 text-amber-200" />
                <span className="hidden xs:inline font-semibold">Program ambasadorski</span>
              </span>
              <span className="text-white/90">{text}</span>
            </p>
            <Link
              href={ctaHref}
              className="group shrink-0 inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full bg-white text-violet-700 text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md transition-all"
            >
              <span className="hidden xs:inline sm:inline">{ctaText}</span>
              <span className="xs:hidden sm:hidden">Więcej</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <button
              onClick={dismiss}
              aria-label="Zamknij baner"
              className="shrink-0 p-1 rounded-md text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
